import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Observable, Subscription, finalize } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiCheckbox, OuiTextfield } from 'omnium-ui/form-field';
import { OuiSelectFilter } from 'omnium-ui/select-filter';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { appRoutePaths } from '../../../../app.paths';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import { DuplicatedArianeInvestorsPage } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { atlasInvestorStatusOptions } from '../shared/atlas-investor-filter-options';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { AtlasDuplicateListPageBase } from '../shared/atlas-duplicate-list-page.base';
import { createFrenchPaginatorIntl } from '../shared/atlas-list-page.paginator';

@Component({
  selector: 'dbw-duplicated-ariane-page',
  imports: [
    MatPaginatorModule,
    MatTableModule,
    RouterLink,
    OuiButtonModule,
    OuiCheckbox,
    OuiSelectFilter,
    OuiTextfield,
    OuiTableModule,
    ConsultantCardComponent,
    InvestorCardComponent,
    StateCardComponent,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: createFrenchPaginatorIntl,
    },
  ],
  templateUrl: './doublons-ariane.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../shared/atlas-duplicate-list-page.shared.scss', './doublons-ariane.component.scss'],
})
export class DuplicatedArianePageComponent extends AtlasDuplicateListPageBase<DuplicatedArianeInvestorsPage> {
  readonly appRoutePaths = appRoutePaths;
  readonly displayedColumns = ['arianeId', 'investors', 'consultants'];
  readonly exporting = signal(false);
  readonly excludeContacts = signal(false);
  readonly differentConsultantsOnly = signal(false);
  readonly investorCodeFilter = signal('');
  readonly investorNameFilter = signal('');
  readonly investorStatusFilter = signal<string[]>([]);
  readonly consultantIdFilter = signal('');
  readonly consultantCodeFilter = signal('');
  readonly consultantNameFilter = signal('');
  readonly investorStatusFilterOptions = atlasInvestorStatusOptions;

  private exportSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly snackbarService: OuiSnackbarService
  ) {
    super();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.exportSubscription?.unsubscribe();
  }

  getCurrentPageInvestorCount(): number {
    return (this.page()?.items ?? []).reduce((total, row) => total + row.duplicateCount, 0);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadPage();
  }

  exportCsv(): void {
    if (this.exporting()) {
      return;
    }

    this.exporting.set(true);
    this.exportSubscription?.unsubscribe();
    this.exportSubscription = this.apiService
      .exportDuplicatedArianeInvestors({
        arianeId: this.parseOptionalNumber(this.primaryFilter()),
        investorId: this.parseOptionalNumber(this.investorIdFilter()),
        investorCode: this.investorCodeFilter(),
        investorName: this.investorNameFilter(),
        investorStatuses: this.investorStatusFilter(),
        consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
        consultantCode: this.consultantCodeFilter(),
        consultantName: this.consultantNameFilter(),
        excludeContacts: this.excludeContacts(),
        differentConsultantsOnly: this.differentConsultantsOnly(),
      })
      .pipe(finalize(() => this.exporting.set(false)))
      .subscribe({
        next: blob => {
          const downloadUrl = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = downloadUrl;
          anchor.download = `doublons-ariane-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
          anchor.click();
          URL.revokeObjectURL(downloadUrl);
          this.snackbarService.open('Export CSV terminé.', 'success');
        },
        error: () => {
          this.snackbarService.open("L'export CSV a échoué.", 'error');
        },
      });
  }

  protected override loadPageRequest(pageIndex: number, pageSize: number): Observable<DuplicatedArianeInvestorsPage> {
    return this.apiService.getDuplicatedArianeInvestorsPage(pageIndex, pageSize, {
      arianeId: this.parseOptionalNumber(this.primaryFilter()),
      investorId: this.parseOptionalNumber(this.investorIdFilter()),
      investorCode: this.investorCodeFilter(),
      investorName: this.investorNameFilter(),
      investorStatuses: this.investorStatusFilter(),
      consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
      consultantCode: this.consultantCodeFilter(),
      consultantName: this.consultantNameFilter(),
      excludeContacts: this.excludeContacts(),
      differentConsultantsOnly: this.differentConsultantsOnly(),
    });
  }

  onInvestorCodeFilterChange(value: string): void {
    this.investorCodeFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorNameFilterChange(value: string): void {
    this.investorNameFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorStatusFilterChange(value: string[]): void {
    this.investorStatusFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantIdFilterChange(value: string): void {
    this.consultantIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantCodeFilterChange(value: string): void {
    this.consultantCodeFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantNameFilterChange(value: string): void {
    this.consultantNameFilter.set(value);
    this.queueFilterReload();
  }

  onExcludeContactsChange(value: boolean): void {
    this.excludeContacts.set(value);
    this.queueFilterReload();
  }

  onDifferentConsultantsOnlyChange(value: boolean): void {
    this.differentConsultantsOnly.set(value);
    this.queueFilterReload();
  }

  protected override getLoadErrorMessage(error: unknown): string {
    const httpError = error as { error?: { detail?: string } };
    return httpError?.error?.detail ?? 'Impossible de charger les doublons Ariane.';
  }

  protected override getPrimaryFilterSummary(): string | null {
    return this.primaryFilter().trim() ? `ArianeId=${this.primaryFilter().trim()}` : null;
  }
}
